import { MAX_DOCUMENT_SIZE_BYTES } from '@/lib/constants'
import { fileNameExtension } from '@/lib/documentRename'
import { isFileTooLarge } from '@/lib/uploadValidation'

export const PAYMENT_PROOF_ACCEPTED_EXT = '.pdf,.jpg,.jpeg,.png'
export const PAYMENT_PROOF_MAX_BYTES = MAX_DOCUMENT_SIZE_BYTES
export const PAYMENT_PROOF_ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
]

export type PaymentProofPreviewFile = {
  id: string
  file: File
}

export type PaymentProofCardItem = {
  id: string
  name: string
  size: number | null
  mime_type?: string | null
  url?: string | null
  file?: File
}

export function formatPaymentProofFileSize(size: number) {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

export function isPaymentProofPdf(file: File) {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
}

export function isPaymentProofFileSupported(file: File) {
  const okType = PAYMENT_PROOF_ACCEPTED_TYPES.includes(file.type)
  const okExt = /\.(pdf|jpe?g|png)$/i.test(file.name)

  return okType || okExt
}

export function isPaymentProofFileTooLarge(file: File) {
  return isFileTooLarge(file, PAYMENT_PROOF_MAX_BYTES)
}

export function buildPaymentProofItems(files: File[]) {
  return files.map(file => ({
    id: `${file.name}-${file.size}-${file.lastModified}`,
    file,
  }))
}

export function mergePaymentProofItems(
  existing: PaymentProofPreviewFile[],
  incoming: PaymentProofPreviewFile[]
) {
  const seen = new Set(existing.map(item => item.id))
  const next = [...existing]

  for (const item of incoming) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    next.push(item)
  }

  return next
}

export function renamePaymentProofItem(
  items: PaymentProofPreviewFile[],
  id: string,
  baseName: string
): PaymentProofPreviewFile[] {
  return items.map(item => {
    if (item.id !== id) return item

    const fileName = baseName + fileNameExtension(item.file.name)
    return {
      ...item,
      file: new File([item.file], fileName, {
        type: item.file.type,
        lastModified: item.file.lastModified,
      }),
    }
  })
}

export function mapPaymentProofPreviewFilesToCardItems(
  items: PaymentProofPreviewFile[]
): PaymentProofCardItem[] {
  return items.map(item => ({
    id: item.id,
    name: item.file.name,
    size: item.file.size,
    mime_type: item.file.type,
    file: item.file,
  }))
}
